import { QueryKey } from '@tanstack/react-query'
import { allPlansQueries } from '../BFF'
import { QueryParams } from '../httpClient/queryString'
import { OpalApiRequestParams } from '../schema'

export const globalQueryKeys = {
  allPlansByWorkspace: (workspaceUuid: string) =>
    allPlansQueries.queryKey({ workspaceUuid }),

  assetUpload: (): QueryKey => ['assetUpload'],
  assetUploadForEntity: (entityResourceType?: string): QueryKey => [
    ...globalQueryKeys.assetUpload(),
    entityResourceType,
  ],
  allGroups: ['userGroups'],
  blockById: (
    workspaceId: string,
    blockId: string,
    query?: QueryParams
  ): QueryKey =>
    query
      ? [...globalQueryKeys.blocksByWorkspace(workspaceId), blockId, { query }]
      : [...globalQueryKeys.blocksByWorkspace(workspaceId), blockId],
  blocksByWorkspace: (workspaceUuid: string, query?: QueryParams) =>
    query ? ['blocks', workspaceUuid, { query }] : ['blocks', workspaceUuid],
  blocksByPlan: (
    workspaceUuid: string,
    planId: string,
    query?: QueryParams
  ): QueryKey =>
    query
      ? ['blocks', 'plan', workspaceUuid, planId, query]
      : ['blocks', 'plan', workspaceUuid, planId],
  boardsByWorkspace: (workspaceUuid: string, query?: QueryParams): QueryKey =>
    query ? ['boards', workspaceUuid, { query }] : ['boards', workspaceUuid],
  bulkOperation: (
    workspaceUuid: string,
    bulkOperationId: string | null,
    query?: QueryParams
  ): QueryKey => [workspaceUuid, 'bulk_operation', bulkOperationId, query],
  contentById: (contentId: string): QueryKey => ['content', contentId],
  /**
   * Every content's version history. Feedback mutations hold a version UUID, not
   * the content UUID a single history is keyed by, so they invalidate the prefix.
   */
  allContentVersions: (): QueryKey => ['content_versions'],
  contentVersions: (contentUuid: string): QueryKey => [
    ...globalQueryKeys.allContentVersions(),
    contentUuid,
  ],
  contentVersionAnnotations: (versionUuid: string): QueryKey => [
    'content_version_annotations',
    versionUuid,
  ],
  contentAccess: (contentId: string): QueryKey => [
    ...globalQueryKeys.contentById(contentId),
    'access',
  ],
  // The v2 and v3 content resources are different documents, so they cannot
  // share `contentById`: whichever fetched first would answer for both.
  contentByIdV3: (contentId: string): QueryKey => ['content', 'v3', contentId],
  customFields: (
    workspaceUuid: string,
    params?: OpalApiRequestParams<'ReadCustomFieldsV3'>
  ): QueryKey =>
    params
      ? ['custom_fields', workspaceUuid, params]
      : ['custom_fields', workspaceUuid],
  customField: (workspaceUuid: string, customFieldId: string): QueryKey => [
    ...globalQueryKeys.customFields(workspaceUuid),
    customFieldId,
  ],
  customFieldTypes: (): QueryKey => ['custom_field_types'],
  customFieldColors: (
    params?: OpalApiRequestParams<'ReadCustomFieldColorsV3'>
  ) => ['customFieldColors', params],
  groups: (
    filter?: QueryParams['filter'],
    include?: QueryParams['include']
  ): QueryKey => [...globalQueryKeys.allGroups, { filter, include }],
  everyoneSocialSettings: (workspaceUuid: string): QueryKey => [
    'everyone_social_settings',
    workspaceUuid,
  ],
  integrationOptionValues: (
    workspaceUuid: string,
    provider: string,
    option: string
  ): QueryKey => ['integration_option_values', workspaceUuid, provider, option],
  labelSets: (workspaceId: string): QueryKey => ['label_sets', workspaceId],
  momentById: (momentId: string, query?: QueryParams): QueryKey =>
    query ? ['moment', momentId, query] : ['moment', momentId],
  momentAccess: (momentId: string): QueryKey => [
    ...globalQueryKeys.momentById(momentId),
    'access',
    {
      embedded: Boolean(window.embedded),
    },
  ],
  momentsByWorkspace: (workspaceUuid: string, query?: QueryParams) =>
    query ? ['moments', workspaceUuid, query] : ['moments', workspaceUuid],
  /** The cover picture of one moment, or without a uuid, of every moment. */
  momentCovers: (momentId?: string): QueryKey =>
    momentId === undefined
      ? ['moments', 'covers']
      : ['moments', 'covers', momentId],
  planningStatuses: (): QueryKey => ['planning_statuses'],
  planById: (worksapceUuid: string, planId: string) => [
    worksapceUuid,
    'plans',
    planId,
  ],
  plansByWorkspace: (worksapceUuid: string, query: QueryParams) =>
    query ? ['plans', worksapceUuid, { query }] : ['plans', worksapceUuid],
  users: (workspaceIdOrMe?: string, query?: QueryParams): QueryKey =>
    query ? ['users', workspaceIdOrMe, { query }] : ['users', workspaceIdOrMe],
  usersById: (
    ids: Array<string>,
    filter?: QueryParams['filter'],
    include?: QueryParams['include']
  ) => ['users', { ids, filter, include }],
  services: (workspaceId: string): QueryKey => ['services', workspaceId],
  stories: (): QueryKey => ['stories'],
  storyById: (storyId: string): QueryKey => ['story', storyId],
  storyAccess: (storyId: string): QueryKey => [
    ...globalQueryKeys.storyById(storyId),
    'access',
  ],
  authenticatedUser: (): QueryKey => [...globalQueryKeys.users('me')],
  workflowByContentId: (
    contentId: string | number,
    accountId?: number
  ): QueryKey => ['workflow', contentId, 'account', accountId],
  /** Every set of moment workflows, whatever it was read for. */
  workflowsByMoments: (): QueryKey => ['workflows', 'moments'],
  /**
   * The workflows over a named set of moments — a month window, say.
   *
   * The moments themselves are not in the key: a thousand of them would make a
   * key nobody can read and one that is rebuilt on every draw. The scope names
   * the set instead, so whoever changes what the scope holds has to invalidate
   * it.
   */
  workflowsByMomentScope: (scope: string): QueryKey => [
    ...globalQueryKeys.workflowsByMoments(),
    scope,
  ],
  workspaceDefaults: (workspaceId: string): QueryKey => [
    'workspaces',
    workspaceId,
    'defaults',
  ],
  workspaceUsers: (workspaceId: string): QueryKey => [
    ...globalQueryKeys.users(workspaceId),
  ],
  workspaceUsersAndGroups: (workspaceId: string): QueryKey => [
    ...globalQueryKeys.users(workspaceId),
    ...globalQueryKeys.groups(),
  ],
  workspaceStamps: (workspaceId: string, query?: QueryParams): QueryKey => [
    'stamps',
    workspaceId,
    query,
  ],
  workspaceStories: (workspaceId: number): QueryKey => [
    ...globalQueryKeys.stories(),
    workspaceId,
  ],
  viewsByWorkspace: (workspaceId: string, query?: QueryParams) => [
    workspaceId,
    'views',
    query,
  ],
}
